import { useCallback, useMemo, useRef, useState } from 'react'
import { Settings, Sparkles } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  pointerWithin,
  rectIntersection,
  type CollisionDetection,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'

import {
  AiComposeModal,
  Button,
  CreateIssueModal,
  EmptyState,
  ErrorBanner,
  FilterBar,
  IssueDetailPanel,
  LoadingPane,
  Select,
  cn,
  formatDay,
  matchesFilters,
  pluralize,
  projectRef,
  useAiConnection,
  AiConnectPrompt,
  useDisclosure,
  useHotkey,
  useIssueFilters,
  useProjectSelection,
  useRoster,
  useSprints,
  useWorkspace,
  type BoardColumnView,
  type CreateIssueDefaults,
  type Issue,
} from '@/features/issue'
import { NewProjectButton } from '@/features/project'

import { BoardColumn } from './BoardColumn'
import { IssueCardPreview } from './IssueCard'
import { useBoardMove, useBoardQuery } from './useBoard'

function columnIndexFor(columns: BoardColumnView[], id: string): number {
  const direct = columns.findIndex((column) => column.id === id)
  if (direct !== -1) return direct
  return columns.findIndex((column) => column.issues.some((issue) => issue.id === id))
}

interface Origin {
  columnId: string
  afterId: string | null
  beforeId: string | null
}

/**
 * The Kanban board.
 *
 * Filters narrow what is on screen but never what is dragged against: the drop
 * still reports the two neighbours the user actually saw, which is what the
 * move endpoint needs to compute a rank. Ordering, column membership and the
 * legacy status are all decided by the server; this component's optimistic
 * update exists only so the card does not visibly snap back on a good drop.
 */
export default function BoardPage() {
  const [params, setParams] = useSearchParams()
  const { slug, organization, userId } = useWorkspace()
  const { projectKey, project, projects, isLoading: projectsLoading } = useProjectSelection(
    organization?.id ?? null,
  )
  const { canWrite } = useRoster(slug)
  const filterController = useIssueFilters()
  const { filters } = filterController

  const scope = params.get('sprint') ?? 'active'
  const openIssueRef = params.get('issue')

  const board = useBoardQuery(slug, projectKey, scope)
  const { data: sprints = [] } = useSprints(slug, projectKey, false)
  const { move, error: moveError, clearError } = useBoardMove(slug, projectKey, scope)

  const create = useDisclosure()
  const compose = useDisclosure()
  // The credential is per workspace, so this is a per-workspace question.
  // Hidden entirely when this workspace has not connected Claude: an entry
  // point that always errors is worse than no entry point. An admin gets a
  // link to the setting instead; a member gets nothing, because there is
  // nothing they could do with it.
  const ai = useAiConnection()
  const [createDefaults, setCreateDefaults] = useState<CreateIssueDefaults>({})
  const [activeIssue, setActiveIssue] = useState<Issue | null>(null)
  const [preview, setPreview] = useState<BoardColumnView[] | null>(null)
  const originRef = useRef<Origin | null>(null)
  const lastOverRef = useRef<string | null>(null)

  const openCreate = useCallback(
    (defaults: CreateIssueDefaults = {}) => {
      setCreateDefaults({ projectKey, ...defaults })
      create.open()
    },
    [create, projectKey],
  )

  useHotkey('c', () => openCreate(), Boolean(canWrite && projectKey))

  const setParam = useCallback(
    (key: string, value: string | null) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value) next.set(key, value)
          else next.delete(key)
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  /** The board as it is shown: filters applied, ranks untouched. */
  const filtered = useMemo<BoardColumnView[]>(() => {
    const columns = board.data?.columns ?? []
    return columns.map((column) => ({
      ...column,
      issues: column.issues.filter((issue) => matchesFilters(issue, filters, userId)),
    }))
  }, [board.data, filters, userId])

  const hiddenPerColumn = useMemo(() => {
    const map = new Map<string, number>()
    for (const column of board.data?.columns ?? []) {
      const shown = filtered.find((entry) => entry.id === column.id)?.issues.length ?? 0
      map.set(column.id, column.issues.length - shown)
    }
    return map
  }, [board.data, filtered])

  const columns = preview ?? filtered

  // Stable identities: an inline arrow here would give every column a new prop
  // on each render and defeat the memo on BoardColumn/IssueCard entirely.
  const handleOpenIssue = useCallback((ref: string) => setParam('issue', ref), [setParam])
  const handleCreateInColumn = useCallback(
    (columnId: string) => openCreate({ columnId }),
    [openCreate],
  )

  // Collision detection for a multi-column board.
  //
  // closestCorners alone cannot drop into an EMPTY column. It ranks droppables
  // by the distance between corners, and an empty column is a single tall
  // rectangle whose corners sit far from the cursor, while a card still sitting
  // in the source column has a corner right next to it. The source card wins
  // every comparison, `over` never becomes the empty column, and the drop
  // silently does nothing.
  //
  // Pointer position is what the user actually means, so try that first; fall
  // back to rectangle overlap while the pointer is between columns, and only
  // then to corner distance.
  const collisionDetection = useCallback<CollisionDetection>((args) => {
    const byPointer = pointerWithin(args)
    if (byPointer.length > 0) return byPointer
    const byRect = rectIntersection(args)
    if (byRect.length > 0) return byRect
    return closestCorners(args)
  }, [])

  const sensors = useSensors(
    // 4px of travel before a drag begins, so a click still opens the issue.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const onDragStart = (event: DragStartEvent) => {
    const issue = event.active.data.current?.issue as Issue | undefined
    if (!issue) return
    setActiveIssue(issue)
    const columnIndex = columnIndexFor(filtered, issue.id)
    const column = filtered[columnIndex]
    if (column) {
      const index = column.issues.findIndex((entry) => entry.id === issue.id)
      originRef.current = {
        columnId: column.id,
        afterId: column.issues[index - 1]?.id ?? null,
        beforeId: column.issues[index + 1]?.id ?? null,
      }
    }
    lastOverRef.current = null
    setPreview(filtered)
  }

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over) return
    // onDragOver fires on every pointer move. Without this guard the board array
    // was rebuilt — new identities for both columns and the dragged card — on
    // each frame, re-rendering every card. Only recompute when the pair the
    // pointer is over actually changes.
    const overKey = `${String(active.id)}:${String(over.id)}`
    if (lastOverRef.current === overKey) return
    lastOverRef.current = overKey
    setPreview((current) => {
      const source = current ?? filtered
      const from = columnIndexFor(source, String(active.id))
      const to = columnIndexFor(source, String(over.id))
      if (from === -1 || to === -1) return source

      // Same column: reorder live. This used to `return source` unchanged,
      // which meant SortableContext's items array never changed, so no gap
      // opened and the card looked stuck — you had to drag it out of the
      // column and back to see anything move. Reordering here is what gives
      // dnd-kit something to animate.
      if (from === to) {
        const column = source[from]!
        const oldIndex = column.issues.findIndex((issue) => issue.id === String(active.id))
        // Hovering the column's own empty space (over.id === column.id) means
        // "past the last card", not a specific position.
        const overIndex =
          String(over.id) === column.id
            ? column.issues.length - 1
            : column.issues.findIndex((issue) => issue.id === String(over.id))
        if (oldIndex === -1 || overIndex === -1 || oldIndex === overIndex) return source
        const next = [...source]
        next[from] = { ...column, issues: arrayMove(column.issues, oldIndex, overIndex) }
        return next
      }

      const fromColumn = source[from]!
      const toColumn = source[to]!
      const moving = fromColumn.issues.find((issue) => issue.id === String(active.id))
      if (!moving) return source

      const overIndex = toColumn.issues.findIndex((issue) => issue.id === String(over.id))
      const insertAt = overIndex === -1 ? toColumn.issues.length : overIndex

      const next = [...source]
      next[from] = {
        ...fromColumn,
        issues: fromColumn.issues.filter((issue) => issue.id !== moving.id),
      }
      const target = [...toColumn.issues]
      target.splice(insertAt, 0, { ...moving, column_id: toColumn.id, status: toColumn.legacy_status })
      next[to] = { ...toColumn, issues: target }
      return next
    })
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { over } = event
    const source = preview ?? filtered
    const dragged = activeIssue
    setPreview(null)
    setActiveIssue(null)
    lastOverRef.current = null
    const origin = originRef.current
    originRef.current = null

    if (!over || !dragged) return

    const toIndex = columnIndexFor(source, String(over.id))
    if (toIndex === -1) return
    const column = source[toIndex]!

    let issues = [...column.issues]
    const currentIndex = issues.findIndex((issue) => issue.id === dragged.id)
    let overIndex = issues.findIndex((issue) => issue.id === String(over.id))

    if (currentIndex === -1) {
      if (overIndex === -1) overIndex = issues.length
      issues.splice(overIndex, 0, dragged)
    } else if (overIndex !== -1 && overIndex !== currentIndex) {
      issues = arrayMove(issues, currentIndex, overIndex)
    }

    const finalIndex = issues.findIndex((issue) => issue.id === dragged.id)
    const after = issues[finalIndex - 1] ?? null
    const before = issues[finalIndex + 1] ?? null

    const unchanged =
      origin !== null &&
      origin.columnId === column.id &&
      origin.afterId === (after?.id ?? null) &&
      origin.beforeId === (before?.id ?? null)
    if (unchanged) return

    move({
      issue: dragged,
      toColumnId: column.id,
      status: column.legacy_status,
      after,
      before,
    })
  }

  const totalShown = columns.reduce((sum, column) => sum + column.issues.length, 0)

  return (
    <div className="flex h-dvh min-h-0 flex-col">
      <header className="shrink-0 space-y-3 border-b border-ink-200 px-4 py-3 dark:border-ink-800">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-sm font-semibold text-ink-900 dark:text-ink-100">
            {project?.name ?? 'Board'}
          </h1>

          {projects.length > 1 && (
            <Select
              aria-label="Project"
              value={projectKey ?? ''}
              onChange={(event) => setParam('project', event.target.value)}
              className="w-44 py-1 text-xs"
            >
              {projects.map((entry) => (
                <option key={entry.id} value={projectRef(entry)}>
                  {entry.name}
                </option>
              ))}
            </Select>
          )}

          <Select
            aria-label="Board scope"
            value={scope}
            onChange={(event) => setParam('sprint', event.target.value)}
            className="w-44 py-1 text-xs"
          >
            <option value="active">Active sprint</option>
            <option value="all">All issues</option>
            <option value="backlog">Backlog only</option>
            {sprints.map((sprint) => (
              <option key={sprint.id} value={sprint.id}>
                {sprint.name}
              </option>
            ))}
          </Select>

          {board.data?.sprint && (
            <span className="text-xs text-ink-500">
              {board.data.sprint.name}
              {board.data.sprint.end_date ? ` · ends ${formatDay(board.data.sprint.end_date)}` : ''}
            </span>
          )}

          <span className="ml-auto text-xs text-ink-500">{pluralize(totalShown, 'issue')}</span>

          {ai.connected ? (
            <Button
              size="sm"
              onClick={compose.open}
              disabled={!canWrite || !projectKey}
              title="Draft several issues from one description"
            >
              <Sparkles className="size-4" aria-hidden="true" />
              Draft with AI
            </Button>
          ) : ai.canManage && ai.settingsPath ? (
            <AiConnectPrompt to={ai.settingsPath} />
          ) : null}

          <Button
            variant="primary"
            size="sm"
            onClick={() => openCreate()}
            disabled={!canWrite || !projectKey}
            title="Create issue (c)"
          >
            Create
          </Button>

          {/* The way into project settings. It lives in the board header
              because that is where someone is standing when they think "I
              cannot change anything about this board" — the columns are the
              first section it opens on. */}
          {projectKey && slug ? (
            <Link
              to={`/${slug}/projects/${encodeURIComponent(projectKey)}/settings?tab=columns`}
              title="Columns, WIP limits, project details"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-ink-300 bg-white px-2.5 text-xs font-medium text-ink-700 transition hover:bg-ink-50 hover:text-ink-900"
            >
              <Settings className="size-3.5" aria-hidden="true" />
              Settings
            </Link>
          ) : null}
        </div>

        <FilterBar slug={slug} controller={filterController} showStatus={false} />
      </header>

      {moveError ? (
        <div className="px-4 pt-3">
          <ErrorBanner error={moveError} onRetry={clearError} />
        </div>
      ) : null}

      <div className="min-h-0 flex-1">
        {board.isLoading || projectsLoading ? (
          <LoadingPane label="Loading board" />
        ) : board.error ? (
          <div className="p-4">
            <ErrorBanner error={board.error} onRetry={() => void board.refetch()} />
          </div>
        ) : !projectKey ? (
          <EmptyState
            title="No project yet"
            description="A board is a view over one project's issues. Create a project and this fills in."
            action={<NewProjectButton variant="primary" />}
          />
        ) : columns.length === 0 ? (
          <EmptyState
            title="This board has no columns"
            description="Columns live in project settings."
            action={
              projectKey && slug ? (
                <Link
                  to={`/${slug}/projects/${encodeURIComponent(projectKey)}/settings?tab=columns`}
                  className="text-sm font-medium text-brand-700 hover:underline"
                >
                  Open project settings
                </Link>
              ) : null
            }
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDragEnd={onDragEnd}
            onDragCancel={() => {
              setPreview(null)
              setActiveIssue(null)
              originRef.current = null
              lastOverRef.current = null
            }}
          >
            <div className={cn('flex h-full min-h-0 gap-3 overflow-x-auto px-4 py-3')}>
              {columns.map((column) => (
                <BoardColumn
                  key={column.id}
                  column={column}
                  canWrite={canWrite}
                  activeIssueId={activeIssue?.id ?? null}
                  hiddenCount={hiddenPerColumn.get(column.id) ?? 0}
                  onOpenIssue={handleOpenIssue}
                  onCreate={handleCreateInColumn}
                />
              ))}
            </div>

            <DragOverlay dropAnimation={null}>
              {activeIssue ? <IssueCardPreview issue={activeIssue} /> : null}
            </DragOverlay>
          </DndContext>
        )}
      </div>

      <CreateIssueModal
        open={create.isOpen}
        onClose={create.close}
        slug={slug}
        projects={projects}
        defaults={createDefaults}
        onCreated={(issue) => setParam('issue', issue.issue_key ?? issue.id)}
      />

      {/* `|| compose.isOpen` is not belt-and-braces. A generation can fail with
          "reconnect required", which re-reads the session and flips
          `ai.connected` to false — unmounting the dialog mid-sentence and
          leaving the person who pressed Generate with no explanation at all.
          While it is open it stays mounted, so its error banner and the
          Connect Claude link it carries get to be read. */}
      {(ai.connected || compose.isOpen) && (
        <AiComposeModal
          open={compose.isOpen}
          onClose={compose.close}
          slug={slug}
          projects={projects}
          defaults={{
            projectKey,
            // The board is showing one sprint; drafts land in the same one.
            sprintId: board.data?.sprint?.id ?? null,
          }}
        />
      )}

      <IssueDetailPanel
        slug={slug}
        issueRef={openIssueRef}
        onClose={() => setParam('issue', null)}
        onOpenIssue={(ref) => setParam('issue', ref)}
      />
    </div>
  )
}
